// components/Survey/SurveyList.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import SurveyForm from './SurveyForm';
import SurveyDetailsModal from './SurveyDetailsModal';
import { surveyService } from '../../services/surveyService';
import { useAuth } from '../../contexts/AuthContext';
import {
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EllipsisVerticalIcon,
  DocumentTextIcon,
  FolderIcon
} from '@heroicons/react/24/outline';

/* UI-only port to the Odoo-style chrome, matching EmployeeList.jsx /
   CompaniesList.jsx. Every piece of behaviour is unchanged: same state,
   same service calls, same filters, same role gating (isSuperAdmin /
   isCompanyAdmin), same actions and modal. What changed is presentation —
   the manual card list + hand-rolled pagination is now a DataTable (built-in
   pagination), same as every other list screen in the app — and colours
   resolve through theme.css vars, so this screen follows light/dark with no
   extra work. */

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'expired', label: 'Expired' }
];

const SurveyList = () => {
  const { isSuperAdmin, isCompanyAdmin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [actionDropdown, setActionDropdown] = useState(null);
  const dropdownRefs = useRef({});

  useEffect(() => {
    if (isAuthenticated) {
      loadSurveys();
    }
  }, [isAuthenticated]);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await surveyService.getSurveys(true);

      console.log('📊 Loaded surveys with categories:', data);

      const surveysWithCategoryCount = data.map(survey => ({
        ...survey,
        categoryCount: survey.categories?.length || 0
      }));

      setSurveys(surveysWithCategoryCount);
    } catch (err) {
      console.error('❌ Error loading surveys:', err);
      setError('Failed to load surveys: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this survey?')) return;
    try {
      await surveyService.deleteSurvey(id);
      await loadSurveys();
      setActionDropdown(null);
    } catch (err) {
      console.error(err);
      setError('Failed to delete survey: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEdit = (survey) => {
    setEditingSurvey(survey);
    setShowForm(true);
    setActionDropdown(null);
  };

  const handleView = (survey) => {
    setSelectedSurvey(survey);
    setShowDetailsModal(true);
    setActionDropdown(null);
  };

  const handleManageCategories = (survey) => {
    navigate('/survey-categories-manager', {
      state: {
        selectedSurveyId: survey.id,
        selectedSurveyName: survey.name,
        fromSurveyList: true
      }
    });
    setActionDropdown(null);
  };

  const handleActionClick = (surveyId, e) => {
    e.stopPropagation();
    setActionDropdown(actionDropdown === surveyId ? null : surveyId);
  };

  // Get survey status
  const getSurveyStatus = (survey) => {
    if (!survey.isActive) return 'inactive';

    const now = new Date();
    const startDate = survey.startDate ? new Date(survey.startDate) : null;
    const endDate = survey.endDate ? new Date(survey.endDate) : null;

    if (startDate && now < startDate) return 'scheduled';
    if (endDate && now > endDate) return 'expired';
    return 'active';
  };

  // Filter surveys based on search and status
  const filteredSurveys = useMemo(() => {
    let filtered = surveys;

    if (searchTerm.trim()) {
      filtered = filtered.filter(survey =>
        survey.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        survey.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        survey.company?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        survey.createdByUser?.userName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(survey => getSurveyStatus(survey).toLowerCase() === filterStatus.toLowerCase());
    }

    return filtered;
  }, [surveys, searchTerm, filterStatus]);

  // Helper function to format dates in dd/MM/yyyy format
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilterStatus('all');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedInsideDropdown = false;

      Object.values(dropdownRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) {
          clickedInsideDropdown = true;
        }
      });

      if (!clickedInsideDropdown) {
        setActionDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const badge = (tone) => ({
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background: `var(--b-${tone}-bg)`,
    color: `var(--b-${tone}-fg)`
  });

  const statusTone = (status) => ({
    active: 'green',
    inactive: 'grey',
    scheduled: 'blue',
    expired: 'red'
  }[status] || 'grey');

  const statusLabel = (status) => ({
    active: 'Active',
    inactive: 'Inactive',
    scheduled: 'Scheduled',
    expired: 'Expired'
  }[status] || status);

  const columns = [
    {
      name: '',
      cell: () => (
        <div
          className="flex h-7 w-7 items-center justify-center rounded-full"
          style={{ background: 'var(--tint)', color: 'var(--tint-ink)' }}
        >
          <DocumentTextIcon className="h-[15px] w-[15px]" />
        </div>
      ),
      width: '54px',
      center: true
    },
    {
      name: 'Survey',
      selector: row => row.name,
      sortable: true,
      cell: row => (
        <div className="min-w-0 py-1">
          <div className="truncate text-[12.5px] font-semibold" style={{ color: 'var(--ink)' }}>
            {row.name}
          </div>
          <div className="truncate text-[11px]" style={{ color: 'var(--faint)' }}>
            {row.description || 'No description provided'}
          </div>
        </div>
      ),
      minWidth: '240px',
      grow: 1
    },
    {
      name: 'Company',
      selector: row => row.company?.name,
      sortable: true,
      cell: row => (
        <span className="text-[12.5px]" style={{ color: row.company?.name ? 'var(--body-2)' : 'var(--dim)' }}>
          {row.company?.name || '—'}
        </span>
      ),
      minWidth: '140px'
    },
    {
      name: 'Period',
      cell: row => (
        <span className="text-[12px]" style={{ color: 'var(--body-2)' }}>
          {formatDate(row.startDate)} – {formatDate(row.endDate)}
        </span>
      ),
      minWidth: '160px'
    },
    {
      name: 'Status',
      selector: row => getSurveyStatus(row),
      sortable: true,
      cell: row => {
        const status = getSurveyStatus(row);
        return <span style={badge(statusTone(status))}>{statusLabel(status)}</span>;
      },
      width: '110px'
    },
    {
      name: 'Categories',
      selector: row => row.categoryCount,
      sortable: true,
      cell: row => <span style={badge('blue')}>{row.categoryCount || 0}</span>,
      width: '110px'
    },
    {
      name: 'Responses',
      selector: row => row.responses?.length,
      sortable: true,
      cell: row => <span style={badge('green')}>{row.responses?.length || 0}</span>,
      width: '110px'
    },
    {
      name: 'Created By',
      selector: row => row.createdByUser?.userName,
      sortable: true,
      cell: row => (
        <span className="text-[12.5px]" style={{ color: row.createdByUser?.userName ? 'var(--body-2)' : 'var(--dim)' }}>
          {row.createdByUser?.userName || '—'}
        </span>
      ),
      minWidth: '130px'
    },
    {
      name: '',
      cell: row => (
        <div
          ref={el => dropdownRefs.current[row.id] = el}
          className="action-dropdown-container relative"
        >
          <button
            onClick={(e) => handleActionClick(row.id, e)}
            className="rounded-[3px] p-1.5 transition-colors"
            style={{ color: 'var(--dim)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--hover)'; e.currentTarget.style.color = 'var(--body)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dim)'; }}
          >
            <EllipsisVerticalIcon className="h-4 w-4" />
          </button>

          {actionDropdown === row.id && (
            <div
              className="absolute right-0 z-50 mt-1 w-48 -translate-y-1 transform overflow-hidden rounded-[4px] py-1"
              style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow)' }}
            >
              <button
                onClick={(e) => { e.stopPropagation(); handleView(row); }}
                className="flex w-full items-center px-3 py-[7px] text-left text-[12.5px] transition-colors"
                style={{ color: 'var(--body)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <EyeIcon className="mr-2.5 h-4 w-4" style={{ color: 'var(--accent)' }} />
                View Details
              </button>

              {isSuperAdmin && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
                    className="flex w-full items-center px-3 py-[7px] text-left text-[12.5px] transition-colors"
                    style={{ color: 'var(--body)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <PencilIcon className="mr-2.5 h-4 w-4" style={{ color: 'var(--accent)' }} />
                    Edit Survey
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleManageCategories(row); }}
                    className="flex w-full items-center px-3 py-[7px] text-left text-[12.5px] transition-colors"
                    style={{ color: 'var(--body)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <FolderIcon className="mr-2.5 h-4 w-4" style={{ color: 'var(--accent)' }} />
                    Manage Categories
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
                    className="flex w-full items-center px-3 py-[7px] text-left text-[12.5px] transition-colors"
                    style={{ color: 'var(--neg)', borderTop: '1px solid var(--line-soft)', marginTop: 4, paddingTop: 9 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <TrashIcon className="mr-2.5 h-4 w-4" />
                    Delete Survey
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ),
      width: '56px',
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  // Custom styles for DataTable — flat, hairline, token-driven.
  const customStyles = {
    table: { style: { backgroundColor: 'var(--surface)' } },
    head: {
      style: {
        fontSize: '11.5px',
        fontWeight: '600',
        color: 'var(--muted)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    headRow: {
      style: {
        minHeight: '32px',
        borderBottomWidth: '1px',
        borderBottomColor: 'var(--line)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    headCells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
        fontWeight: '600',
        letterSpacing: '0',
        textTransform: 'none',
        fontSize: '11.5px',
        color: 'var(--muted)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    cells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingTop: '8px',
        paddingBottom: '8px',
        fontSize: '12.5px',
        color: 'var(--body-2)'
      }
    },
    rows: {
      style: {
        minHeight: '48px',
        backgroundColor: 'var(--surface)',
        color: 'var(--body-2)',
        '&:not(:last-of-type)': {
          borderBottomWidth: '1px',
          borderBottomColor: 'var(--line-soft)'
        },
        '&:hover': { backgroundColor: 'var(--row-sel)' }
      },
      stripedStyle: { backgroundColor: 'var(--surface)' }
    },
    pagination: {
      style: {
        backgroundColor: 'var(--surface)',
        color: 'var(--muted)',
        fontSize: '12px',
        borderTopWidth: '1px',
        borderTopColor: 'var(--line)',
        minHeight: '42px'
      },
      pageButtonsStyle: {
        fill: 'var(--muted)',
        '&:disabled': { fill: 'var(--dim)' },
        '&:hover:not(:disabled)': { backgroundColor: 'var(--hover)' }
      }
    },
    noData: { style: { backgroundColor: 'var(--surface)' } },
    progress: { style: { backgroundColor: 'var(--surface)' } }
  };

  const LoadingComponent = () => (
    <div className="flex items-center justify-center py-12" style={{ background: 'var(--surface)' }}>
      <div
        className="h-6 w-6 animate-spin rounded-full border-b-2"
        style={{ borderColor: 'var(--accent)' }}
      />
      <span className="ml-3 text-[12.5px]" style={{ color: 'var(--muted)' }}>Loading surveys…</span>
    </div>
  );

  const NoDataComponent = () => (
    <div
      className="flex flex-col items-center justify-center px-6 py-14 text-center"
      style={{ background: 'var(--surface)' }}
    >
      <div
        className="mb-4 flex h-12 w-12 items-center justify-center rounded-full"
        style={{ background: 'var(--tint)' }}
      >
        <DocumentTextIcon className="h-6 w-6" style={{ color: 'var(--tint-ink)' }} />
      </div>
      <h3 className="mb-1 text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>No surveys found</h3>
      <p className="mb-4 text-[12.5px]" style={{ color: 'var(--faint)' }}>
        {searchTerm || filterStatus !== 'all' ? 'Try adjusting your search terms or filters' : 'Get started by creating your first survey'}
      </p>
      {!searchTerm && filterStatus === 'all' && isSuperAdmin && (
        <button
          onClick={() => { setEditingSurvey(null); setShowForm(true); }}
          className="h-7 rounded-[3px] px-3 text-[12.5px] font-semibold"
          style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
        >
          New
        </button>
      )}
      {(searchTerm || filterStatus !== 'all') && (
        <button
          onClick={clearSearch}
          className="text-[12.5px] font-medium"
          style={{ color: 'var(--accent)', background: 'transparent', border: 0, cursor: 'pointer' }}
        >
          Clear search
        </button>
      )}
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-full items-center justify-center" style={{ background: 'var(--bg)' }}>
        <h2 className="text-[16px] font-semibold" style={{ color: 'var(--ink)' }}>Please log in to view surveys</h2>
      </div>
    );
  }

  return (
    <div className="min-h-full" style={{ background: 'var(--bg)' }}>
      {/* ══ CONTROL PANEL ══ */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-[14px] pb-2 pt-[9px]">
          <h1
            className="m-0 truncate text-[18px] font-semibold tracking-[-.2px]"
            style={{ color: 'var(--ink)' }}
          >
            Surveys
          </h1>

          {isSuperAdmin && (
            <div className="flex items-center gap-[6px]">
              <button
                onClick={() => { setEditingSurvey(null); setShowForm(true); }}
                className="h-7 rounded-[3px] px-[13px] text-[12.5px] font-semibold"
                style={{ background: 'var(--accent)', color: 'var(--on-accent)', border: 0, cursor: 'pointer' }}
              >
                New
              </button>
            </div>
          )}

          <div className="min-w-[100px] flex-1" />

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-7 rounded-[3px] px-2 text-[12.5px] outline-none"
            style={{ background: 'var(--surface)', border: '1px solid var(--line-3)', color: 'var(--body)' }}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Search — Odoo control-panel style: one hairline box */}
          <div
            className="flex min-w-[200px] max-w-[420px] flex-1 items-center gap-[6px] rounded-[3px] py-[2px] pl-2 pr-1"
            style={{ background: 'var(--surface)', border: '1px solid var(--line-3)' }}
          >
            <svg
              className="h-[13px] w-[13px] flex-shrink-0"
              style={{ color: 'var(--faint)' }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search name, description, company or creator…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="h-6 min-w-[70px] flex-1 border-0 bg-transparent text-[12.5px] outline-none"
              style={{ color: 'var(--body)' }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="px-1 text-[14px] leading-none"
                style={{ color: 'var(--faint)', background: 'transparent', border: 0, cursor: 'pointer' }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          {/* Record counter */}
          <div className="flex items-center gap-[10px] text-[12px]" style={{ color: 'var(--muted)' }}>
            <span>
              {filteredSurveys.length}{(searchTerm || filterStatus !== 'all') ? ` / ${surveys.length}` : ''}
              {' '}record{filteredSurveys.length === 1 ? '' : 's'}
            </span>
            <span style={{ width: 1, height: 15, background: 'var(--line-3)' }} />
            <span>{surveys.filter(s => getSurveyStatus(s) === 'active').length} active</span>
            <span style={{ width: 1, height: 15, background: 'var(--line-3)' }} />
            <span>{surveys.reduce((acc, s) => acc + (s.categoryCount || 0), 0)} categories</span>
          </div>
        </div>
      </div>

      <div className="p-[14px]">
        {/* Form */}
        {showForm && (
          <div className="mb-[14px]">
            <SurveyForm
              initialData={editingSurvey}
              isEditing={!!editingSurvey}
              onSubmit={async (data) => {
                if (editingSurvey) await surveyService.updateSurvey(editingSurvey.id, data);
                else await surveyService.createSurvey(data);
                setShowForm(false);
                await loadSurveys();
              }}
              onCancel={() => setShowForm(false)}
            />
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div
            className="mb-[14px] flex items-center gap-2 rounded-[4px] px-3 py-2.5 text-[12.5px]"
            style={{ background: 'var(--b-red-bg)', border: '1px solid var(--line)', color: 'var(--b-red-fg)' }}
          >
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {/* Role info */}
        {isCompanyAdmin && (
          <div
            className="mb-[14px] flex items-center gap-2 rounded-[4px] px-3 py-2.5 text-[12.5px]"
            style={{ background: 'var(--tint)', border: '1px solid var(--line)', color: 'var(--tint-ink)' }}
          >
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            You have view-only access to surveys. Only SuperAdmin can create and manage surveys.
          </div>
        )}

        {/* Table */}
        <div
          className="overflow-hidden rounded-[4px]"
          style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}
        >
          <DataTable
            columns={columns}
            data={filteredSurveys}
            progressPending={loading}
            progressComponent={<LoadingComponent />}
            noDataComponent={<NoDataComponent />}
            customStyles={customStyles}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[5, 10, 20, 50]}
            highlightOnHover
            pointerOnHover
            responsive
            persistTableHead
          />
        </div>
      </div>

      {/* Survey Details Modal */}
      {showDetailsModal && selectedSurvey && (
        <SurveyDetailsModal
          survey={selectedSurvey}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
    </div>
  );
};

export default SurveyList;
